import React, { useEffect, useState } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockTitle,
  BlockHeadContent,
  Icon,
  BlockBetween,
} from "../../components/Component";
import BasePaginationTable from "../../components/table/BasePaginationTable";
import { defaultParams } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { getListOrder } from "../../slice/orders";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  formatDateTime,
  formatTotal,
  orderStatuses,
  paymentMethodLabel,
  statusColor,
  statusLabel,
  userName,
} from "./order-utils";

function getDefaultColumns(onViewClick) {
  return [
    {
      head: <span className="sub-text">{"Order"}</span>,
      cell: (row) => <span className="fw-bold">{row.code ?? "N/A"}</span>,
    },
    {
      head: <span className="sub-text">{"Buyer"}</span>,
      cell: (row) => (
        <>
          <div>{userName(row.user)}</div>
          <div className="text-muted small">{row.user?.email ?? ""}</div>
        </>
      ),
    },
    {
      head: <span className="sub-text">{"Seller"}</span>,
      cell: (row) => (
        <>
          <div>{userName(row.seller)}</div>
          <div className="text-muted small">{row.seller?.email ?? ""}</div>
        </>
      ),
    },
    {
      head: <span className="sub-text">{"Items"}</span>,
      cell: (row) => (
        <span>
          {(row.items ?? []).reduce((sum, i) => sum + (i.quantity ?? 0), 0)}
        </span>
      ),
    },
    {
      head: <span className="sub-text">{"Total"}</span>,
      cell: (row) => <span>{formatTotal(row.total, row.currency)}</span>,
    },
    {
      head: <span className="sub-text">{"Payment"}</span>,
      cell: (row) => <span>{paymentMethodLabel(row.paymentMethod)}</span>,
    },
    {
      head: <span className="sub-text">{"Status"}</span>,
      cell: (row) => (
        <span className={`badge badge-dim bg-outline-${statusColor(row.status)}`}>
          {statusLabel(row.status)}
        </span>
      ),
    },
    {
      head: <span className="sub-text">{"Created At"}</span>,
      cell: (row) => <span>{formatDateTime(row.createdAt)}</span>,
    },
    {
      head: <span className="sub-text"></span>,
      cell: (row) => (
        <a
          href="#detail"
          className="link link-light"
          onClick={(ev) => {
            ev.preventDefault();
            onViewClick(row);
          }}
        >
          <Icon name="eye"></Icon>
          <span>{"View"}</span>
        </a>
      ),
    },
  ];
}

const OrdersPage = () => {
  const { list, summary, loading } = useSelector((state) => state.orders);
  const [params, setParams] = useState(defaultParams);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getListOrder(params));
  }, [dispatch, params]);

  const handlePageChange = (page) => {
    setParams({
      ...params,
      page,
    });
  };

  const onView = (order) => {
    navigate(`/orders/${order?._id}`);
  };

  const dataTableColumns = getDefaultColumns(onView);
  const handleSearch = debounce((value) => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      keyword: value,
    }));
  }, 400);

  const handleStatusChange = (status) => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      status: status || undefined,
    }));
  };

  return (
    <React.Fragment>
      <Head title="Orders"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle>Orders</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="d-flex g-2" style={{ gap: 8 }}>
                <select
                  className="form-select"
                  style={{ minWidth: 180 }}
                  value={params.status ?? ""}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <option value="">All statuses</option>
                  {orderStatuses.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <div className="form-control-wrap" style={{ minWidth: 220 }}>
                  <div className="form-icon form-icon-right">
                    <Icon name="search"></Icon>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search order code"
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <BasePaginationTable
          list={list}
          loading={loading}
          columns={dataTableColumns}
          onPageChange={handlePageChange}
          onRowClick={onView}
          hasNextPage={summary?.hasNextPage}
          totalItems={summary?.count}
          {...params}
        />
      </Content>
    </React.Fragment>
  );
};

export default OrdersPage;
